import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/auth/AuthContext";
import constants from "../../Utils/constants";
import axiosInstance from "../../services/authWrapper";
import useTranslator from "../../hooks/useTranslator";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import i18next from "i18next";
import {
  toast,
  validateDniNie,
  cleanIdentityNumber,
} from "../../helper/common";
import { PhoneInput } from "react-international-phone";
import DeleteProfileModal from "./DeleteProfileModal";
import "./editBasicInfo.css";
import "./deleteProfile.css";

const EditBasicInfo = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { user, loadUserTenant, changeUserLng } = useContext(AuthContext);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [language, setLanguage] = useState(
    localStorage.getItem("i18nextLng") || "en-us"
  );
  const [showDelete, setShowDelete] = useState(false);
  const isKycVerified = !!user?.onfido_user_kyc;

  useEffect(() => {
    if (!user) return;
    setForm({
      fname: user.fname || "",
      lname: user.lname || "",
      dob: user.dob ? user.dob.split("T")[0] : "",
      email: user.email || "",
      phone_number: user.phone_number || "",
      post_code: String(user.post_code || ""),
      employment_status: user.employment_status || "",
      employment_category: user.employment_category || "",
      current_annual_income: user.current_annual_income ?? "",
      currency: user.currency || "EUR",
      document_type: user.passport_id && !user.dni_nie ? "passport" : "dni_nie",
      identity_number: user.dni_nie || user.passport_id || "",
    });
  }, [user]);

  const requiresSubcategory = [
    constants.public_servant,
    constants.employed,
    constants.self_employed,
  ].includes(form.employment_status);

  const validate = (values = form) => {
    const next = {};
    if (!values.fname.trim())
      next.fname = _t("firstNameIsRequired", "First name is required");
    else if (!/^[A-Za-zÀ-ÿ\s'-]+$/.test(values.fname))
      next.fname = _t("firstNameInvalid", "Enter a valid first name");
    if (!values.lname.trim())
      next.lname = _t("lastNameIsRequired", "Last name is required");
    else if (!/^[A-Za-zÀ-ÿ\s'-]+$/.test(values.lname))
      next.lname = _t("lnameNameInvalid", "Enter a valid last name");
    if (!values.dob)
      next.dob = _t("dateOfBirthIsRequired", "Date of birth is required");
    else if (new Date(values.dob) < new Date("1900-01-01"))
      next.dob = _t(
        "dateOfBirthCannotBeBefore1900",
        "Date of birth cannot be before 1900"
      );
    else if (new Date(values.dob) > new Date())
      next.dob = _t(
        "dateOfBirthCannotBeInFuture",
        "Date of birth cannot be in the future"
      );
    if (!values.email.trim())
      next.email = _t("emailIsRequired", "Email is required");
    else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email.trim())
    )
      next.email = _t("invalidEmailFormat", "Enter a valid email");
    if (!isKycVerified) {
      if (!values.identity_number || !values.identity_number.trim())
        next.identity_number = _t(
          "documentNumberIsRequired",
          "Document number is required"
        );
      else if (!/^[A-Za-z0-9]+$/.test(values.identity_number))
        next.identity_number = _t(
          "onlyLettersAndNumbersAreRequired",
          "Only letters and numbers are allowed"
        );
      else if (values.identity_number.length > 9)
        next.identity_number = _t(
          "documentNumberCannotExceedNineCharacters",
          "Document number cannot exceed 9 characters"
        );
      else if (values.document_type === "dni_nie") {
        const normalizedIdentity = values.identity_number.trim().toUpperCase();
        const dniNieType = /^[XYZ]/.test(normalizedIdentity) ? "nie" : "dni";
        const hasValidFormat =
          dniNieType === "nie"
            ? /^[XYZ][0-9]{7}[A-Z]$/.test(normalizedIdentity)
            : /^[0-9]{8}[A-Z]$/.test(normalizedIdentity);
        if (
          !hasValidFormat ||
          !validateDniNie(normalizedIdentity, dniNieType)
        ) {
          next.identity_number = _t(
            `invalid${dniNieType}`,
            `Invalid ${dniNieType.toUpperCase()} number`
          );
        }
      }
    }
    if (!values.phone_number || values.phone_number.length <= 5)
      next.phone_number = _t(
        "phoneNumberMustBeAtLeastSixDigits",
        "Enter a valid phone number"
      );
    if (values.post_code.trim() && values.post_code.length > 6)
      next.post_code = _t(
        "postCodeCannotExpeedSixCharacters",
        "Postal code is too long"
      );
    if (!values.employment_status)
      next.employment_status = _t(
        "employmentStatusIsRequired",
        "Employment status is required"
      );
    if (requiresSubcategory && !values.employment_category)
      next.employment_category = _t(
        "employemntCategoryIsRequired",
        "Employment category is required"
      );
    if (
      values.current_annual_income === "" ||
      values.current_annual_income === null ||
      values.current_annual_income === undefined
    ) {
      next.current_annual_income = _t(
        "annualIncomeIsRequired",
        "Annual income is required"
      );
    } else if (
      Number.isNaN(Number(values.current_annual_income)) ||
      Number(values.current_annual_income) < 0
    ) {
      next.current_annual_income = _t(
        "enterAValidIncome",
        "Enter a valid income"
      );
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const update = (name, value) => {
    const next = {
      ...form,
      [name]: value,
      ...(name === "document_type" ? { identity_number: "" } : {}),
    };
    if (name === "employment_status") next.employment_category = "";
    setForm(next);
    setErrors((current) => ({
      ...current,
      [name]: undefined,
      ...(name === "document_type" ? { identity_number: undefined } : {}),
      ...(name === "employment_status"
        ? { employment_category: undefined }
        : {}),
    }));
  };

  const field = (name, label, type = "text", options = {}) => (
    <div className="edit-field-wrapper">
      <label
        className={`edit-field ${errors[name] ? "has-error" : ""} ${options.disabled ? "is-disabled" : ""}`}
      >
        <span>{label}</span>
        {options.select ? (
          <select
            value={form[name] || ""}
            onChange={(event) => update(name, event.target.value)}
            disabled={options.disabled}
          >
            <option value="" disabled>
              {_t("pleaseSelect", "Select")}
            </option>
            {options.select.map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={form[name] || ""}
            onChange={(event) =>
              options.onChange
                ? options.onChange(event)
                : update(name, event.target.value)
            }
            disabled={options.disabled}
          />
        )}
        {options.locked && (
          <span className="lock" aria-hidden="true">
            🔒
          </span>
        )}
      </label>
      {errors[name] && (
        <small className="edit-field-error">{errors[name]}</small>
      )}
    </div>
  );

  const subcategories =
    form.employment_status === constants.public_servant
      ? constants.public_servant_subcategories
      : form.employment_status === constants.employed
        ? constants.employed_subcategories
        : constants.self_employed_subcategories;
  const employmentOptions = [
    [constants.employed, _t("employed", "Employed")],
    [constants.self_employed, _t("selfEmployed", "Self-employed")],
    [constants.student, _t("student", "Student")],
    [constants.unemployed, _t("unemployed", "Unemployed")],
    [constants.retired, _t("retired", "Retired")],
    [constants.public_servant, _t(constants.public_servant, "Public servant")],
    ...(user?.employment_status === constants.company
      ? [[constants.company, _t("company", "Company")]]
      : []),
  ];

  const save = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        dni_nie: form.document_type === "dni_nie" ? form.identity_number : "",
        passport_id:
          form.document_type === "passport" ? form.identity_number : "",
        protected_housing: user.protected_housing || false,
        gurantor_relation: user.gurantor_relation || "",
        role: localStorage.getItem("usertype") || "tenant",
        phone_number: form.phone_number?.replace("+", ""),
        current_annual_income: Number(form.current_annual_income) || 0,
        is_edit: true,
        lang: language,
      };
      delete payload.identity_number;
      delete payload.document_type;
      await axiosInstance.patch(
        `${process.env.REACT_APP_SERVER_URL}/users/profile/${user._id}`,
        payload,
        { headers: { "Accept-Language": language } }
      );
      await loadUserTenant("tenant");
      toast.success(
        _t("profileUpdatedSuccessfully", "Profile updated successfully")
      );
      navigate("/home");
    } catch (error) {
      toast.error(
        error?.response?.data?.errormsg ||
          _t("somethingWentWrong", "Something went wrong")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="edit-basic-page">
      <div className="edit-basic-shell">
        <span
          className="back-link"
          type="button"
          onClick={() => navigate("/home")}
        >
          ← {_t("goBack", "Back to my profile")}
        </span>
        <h1>{_t("editProfile", "Edit profile")}</h1>
        <p className="intro">
          {_t(
            "managePersonalDetails",
            "Manage your personal details, contact information and employment"
          )}
        </p>
        <div className="accent-line" />

        <form className="edit-basic-card" onSubmit={save}>
          <section>
            <div className="section-heading">
              <span>
                <BadgeOutlinedIcon sx={{ fontSize: 18 }} />
              </span>
              <div>
                <h2>{_t("personalDetails", "Personal details")}</h2>
                {isKycVerified && (
                  <p>
                    {_t(
                      "verifiedDocumentCantBeEdited",
                      "Your verified document can't be edited"
                    )}
                  </p>
                )}
              </div>
            </div>
            <div className="edit-grid">
              {field("fname", _t("firstName", "First name"))}
              {field("lname", _t("lastName", "Last name"))}
              <div
                className={`edit-document-type ${
                  isKycVerified ? "is-disabled" : ""
                }`}
                role="radiogroup"
                aria-labelledby="edit-document-type-label"
              >
                <span id="edit-document-type-label">
                  {_t("documentType", "Document type")}
                </span>
                <div className="edit-document-options">
                  <label
                    className={
                      form.document_type === "dni_nie" ? "is-selected" : ""
                    }
                  >
                    <input
                      type="radio"
                      name="document_type"
                      value="dni_nie"
                      checked={form.document_type === "dni_nie"}
                      onChange={(event) =>
                        update("document_type", event.target.value)
                      }
                      disabled={isKycVerified}
                    />
                    <span>{_t("dniNie", "DNI / NIE")}</span>
                  </label>
                  <label
                    className={
                      form.document_type === "passport" ? "is-selected" : ""
                    }
                  >
                    <input
                      type="radio"
                      name="document_type"
                      value="passport"
                      checked={form.document_type === "passport"}
                      onChange={(event) =>
                        update("document_type", event.target.value)
                      }
                      disabled={isKycVerified}
                    />
                    <span>{_t("passport", "Passport")}</span>
                  </label>
                </div>
              </div>
              {field(
                "identity_number",
                form.document_type === "passport"
                  ? _t("passportNumber", "Passport number")
                  : _t("dniNie", "DNI / NIE"),
                "text",
                isKycVerified
                  ? { disabled: true, locked: true }
                  : {
                      onChange: (event) => {
                        const cleaned = cleanIdentityNumber(event.target.value);
                        update("identity_number", cleaned);
                      },
                    }
              )}
              {field("dob", _t("dateofbirth", "Date of birth"), "date")}
            </div>
            {isKycVerified && (
              <p className="verify-note">
                {_t("hasYourDocumentChanged", "Has your document changed?")}{" "}
                <span type="button" onClick={() => navigate("/home?kyc=true")}>
                  {_t("verifyYourIdentityAgain", "Verify your identity again")}
                </span>
              </p>
            )}
          </section>

          <section>
            <div className="section-heading">
              <span>
                <MailOutlineIcon sx={{ fontSize: 18 }} />
              </span>
              <div>
                <h2>{_t("contact", "Contact")}</h2>
                <p>
                  {_t(
                    "contactDetailsDescription",
                    "We use these details to notify you about your applications"
                  )}
                </p>
              </div>
            </div>
            <div className="edit-grid">
              {field("email", _t("email", "Email"), "email")}
              <div className="edit-field-wrapper">
                <label
                  className={`edit-field ${errors.phone_number ? "has-error" : ""}`}
                >
                  <span>{_t("phoneNumber", "Phone number")}</span>
                  <PhoneInput
                    international
                    defaultCountry="es"
                    value={form.phone_number || ""}
                    onChange={(value) => update("phone_number", value)}
                  />
                </label>
                {errors.phone_number && (
                  <small className="edit-field-error">
                    {errors.phone_number}
                  </small>
                )}
              </div>
              {field("post_code", _t("postCode", "Postal code"), "number", {
                onChange: (event) => {
                  if (event.target.value.length <= 5) {
                    update("post_code", event.target.value);
                  }
                },
              })}
              <label className="edit-field">
                <span>{_t("language", "Language")}</span>
                <select
                  value={language}
                  onChange={async (event) => {
                    const val = event.target.value;
                    setLanguage(val);
                    await i18next.changeLanguage(val);
                    changeUserLng();
                  }}
                >
                  <option value="en-us">English</option>
                  <option value="es">Español</option>
                </select>
              </label>
            </div>
            <p className="helper-text">
              {_t(
                "emailVerificationNotice",
                "If you change your email, we'll send a verification link to the new address. It becomes active once verified."
              )}
            </p>
          </section>

          <section>
            <div className="section-heading">
              <span>
                <WorkOutlineIcon sx={{ fontSize: 18 }} />
              </span>
              <div>
                <h2>{_t("employment", "Employment")}</h2>
                <p>
                  {_t(
                    "employmentDetailsDescription",
                    "These details affect your Skor and what landlords see"
                  )}
                </p>
              </div>
            </div>
            <div className="edit-grid">
              {field(
                "employment_status",
                _t("employmentStatus", "Employment status"),
                "text",
                { select: employmentOptions }
              )}
              {requiresSubcategory &&
                field(
                  "employment_category",
                  _t("employmentCategory", "Contract type"),
                  "text",
                  {
                    select: subcategories.map((item) => [item, _t(item, item)]),
                  }
                )}
              {field(
                "current_annual_income",
                _t("averageNetMonthlyIncome", "Average net monthly income"),
                "number"
              )}
            </div>
          </section>

          <div className="edit-actions">
            <button
              type="button"
              className="discard text-capitalize"
              onClick={() => navigate("/home")}
            >
              {_t("discard", "Discard")}
            </button>
            <button type="submit" className="save" disabled={saving}>
              {saving
                ? _t("saving", "Saving...")
                : _t("saveChanges", "Save changes")}
            </button>
          </div>
        </form>

        <section
          className="delete-profile-card"
          aria-labelledby="delete-profile-title"
        >
          <div className="delete-profile-card__content">
            <div className="delete-profile-card__icon" aria-hidden="true">
              <img src="/images/delete-icon.svg" alt="" />
            </div>
            <div>
              <h3
                className="delete-profile-card__title"
                id="delete-profile-title"
              >
                {_t("deleteMyProfile", "Delete my profile")}
              </h3>
              <p className="delete-profile-card__description">
                {_t(
                  "profileWillBeDeactivatedAndNoLongerVisible",
                  "Your profile will be deactivated and no longer visible to landlords and agencies. You can come back anytime."
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="delete-profile-card__button"
            onClick={() => setShowDelete(true)}
          >
            {_t("deleteProfile", "Delete profile")}
          </button>
        </section>
      </div>
      <DeleteProfileModal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        currentLang={language}
      />
    </main>
  );
};

export default EditBasicInfo;
